import { useMemo, useState } from "react";
import { useMediaPreload } from "./useMediaPreload";
import { GenericLoader } from "./layouts/GenericLoader";
import type { JoinLayoutDefinition } from "./layouts";
import type { JoinLayoutProps } from "./layouts/types";

interface PremiumLayoutHostProps extends JoinLayoutProps {
  layoutDef: JoinLayoutDefinition;
  config: unknown;
}

/**
 * Hosts a premium join layout: preloads the theme's media, shows that theme's
 * loader (or GenericLoader as fallback) until ready, then mounts the layout so
 * its own intro animation plays once — never behind the loader. This is the
 * single place the loading/preload concern lives for ALL premium themes.
 */
export function PremiumLayoutHost({ layoutDef, config, ...props }: PremiumLayoutHostProps) {
  const { galleryImages, coverPhotoUrl, ambientVideoUrl, title } = props;

  const media = useMemo(() => {
    if (layoutDef.collectMedia) {
      return layoutDef.collectMedia({ config, galleryImages, coverPhotoUrl, ambientVideoUrl });
    }
    const images = Array.from(
      new Set([...galleryImages, ...(coverPhotoUrl ? [coverPhotoUrl] : [])]),
    );
    return { images, video: ambientVideoUrl };
  }, [layoutDef, config, galleryImages, coverPhotoUrl, ambientVideoUrl]);

  const { progress, ready } = useMediaPreload(media.images, media.video);
  const [loaderGone, setLoaderGone] = useState(false);

  const colors = (config as { colors?: { accent?: string; background?: string } } | undefined)?.colors;
  const accent = colors?.accent ?? "#dc2626";
  const bg = colors?.background ?? "#0a0a0a";

  const Layout = layoutDef.component;
  const Loader = layoutDef.loader ?? GenericLoader;

  return (
    <>
      {ready && <Layout {...props} config={config} />}
      {!loaderGone && (
        <Loader
          progress={progress}
          ready={ready}
          title={title}
          accent={accent}
          bg={bg}
          label=""
          onExited={() => setLoaderGone(true)}
        />
      )}
    </>
  );
}
